import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createProject } from '@/api';
import { isLoggedIn } from '@/auth/token';
import { guestForkPaths } from '@/pages/home/guestFork';
import { seedStudioChatTab, studioProjectPath } from '@/pages/home/studioEntry';
import { releaseComposerContinuePrompt, takeComposerContinuePrompt } from '@/pages/landing/composerGuide';
import { CAPABILITIES, capabilityLabels } from './capabilities';
import BriefForm from './components/BriefForm';
import ChatPanel from './components/ChatPanel';
import {
  TYPE_QUESTION,
  WELCOME,
  applyInterviewTurn,
  briefOutcomeDescription,
  nowTime,
  seedBriefFromPrompt,
} from './interview';
import { EMPTY_BRIEF, PROJECT_TYPES, type Brief, type Message } from './types';

type Props = {
  initialPrompt?: string;
};

export default function ComposerPage({ initialPrompt }: Props) {
  const navigate = useNavigate();
  const seeded = useRef(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [step, setStep] = useState(0);
  const [brief, setBrief] = useState<Brief>(EMPTY_BRIEF);
  const [selected, setSelected] = useState<string[]>([]);
  const [continueErr, setContinueErr] = useState<string | null>(null);
  const [continueBusy, setContinueBusy] = useState(false);

  const push = useCallback((role: Message['role'], text: string) => {
    setMessages((m) => [...m, { id: `${role[0]}-${Date.now()}-${m.length}`, role, text, time: nowTime() }]);
  }, []);

  useEffect(() => {
    document.title = 'Composer · Sourcetape';
  }, []);

  useEffect(() => {
    if (seeded.current) return;
    seeded.current = true;
    const text = (initialPrompt || '').trim() || takeComposerContinuePrompt();
    const seededBrief = seedBriefFromPrompt(text);
    if (seededBrief) {
      setBrief(seededBrief.brief);
      setStep(seededBrief.step);
      setMessages([
        { id: 'u-seed', role: 'user', text: seededBrief.brief.description, time: nowTime() },
        { id: 'a-seed', role: 'assistant', text: TYPE_QUESTION, time: nowTime() },
      ]);
    } else {
      setMessages([{ id: 'a-welcome', role: 'assistant', text: WELCOME, time: nowTime() }]);
    }
    const t = window.setTimeout(() => releaseComposerContinuePrompt(), 0);
    return () => window.clearTimeout(t);
  }, [initialPrompt]);

  const handleSend = useCallback(
    (raw: string) => {
      const result = applyInterviewTurn(step, brief, raw);
      if (!result) return;
      push('user', raw.trim());
      setBrief(result.brief);
      setStep(result.step);
      push('assistant', result.assistant);
    },
    [brief, step, push],
  );

  const handleUpdate = useCallback((patch: Partial<Brief>) => {
    setBrief((b) => ({ ...b, ...patch }));
  }, []);

  const handleToggle = useCallback(
    (id: string) => {
      const cap = CAPABILITIES.find((c) => c.id === id);
      const on = selected.includes(id);
      setSelected((prev) => (on ? prev.filter((x) => x !== id) : [...prev, id]));
      if (cap) {
        push('assistant', on ? `Removed ${cap.label}.` : `Added ${cap.label}.`);
      }
    },
    [push, selected],
  );

  const handleClear = useCallback(() => {
    setSelected([]);
  }, []);

  const handleContinue = useCallback(async () => {
    setContinueErr(null);
    if (!isLoggedIn()) {
      navigate(guestForkPaths('/compose').register);
      return;
    }
    setContinueBusy(true);
    try {
      const created = await createProject({
        title: brief.name.trim() || 'Untitled',
        outcome_description: briefOutcomeDescription(brief, capabilityLabels(selected)),
        outcome_type: (brief.type || 'App').toLowerCase(),
      });
      seedStudioChatTab(created.id);
      navigate(studioProjectPath(created.id), { state: { project: created } });
    } catch (e) {
      setContinueErr(e instanceof Error ? e.message : 'failed to start the build');
    } finally {
      setContinueBusy(false);
    }
  }, [brief, navigate, selected]);

  const choices = step === 1 ? PROJECT_TYPES : null;

  return (
    <div className="h-screen bg-bg text-textp flex flex-col" data-testid="composer-page">
      <main className="flex-1 flex flex-col lg:flex-row min-h-0 overflow-hidden">
        <ChatPanel
          messages={messages}
          typing={false}
          disabled={step >= 5}
          choices={choices}
          onSend={handleSend}
        />
        <BriefForm
          brief={brief}
          step={step}
          selected={selected}
          continueErr={continueErr}
          continueBusy={continueBusy}
          onUpdate={handleUpdate}
          onToggleCapability={handleToggle}
          onClearCapabilities={handleClear}
          onContinue={() => void handleContinue()}
        />
      </main>
    </div>
  );
}

export { TYPE_QUESTION, WELCOME };
